/** @typedef {'invalidWeight' | 'invalidHeight'} BmiErrorCode */
/** @typedef {'underweight' | 'normal' | 'overweight' | 'obese'} BmiCategory */

const MIN_WEIGHT_KG = 1;
const MAX_WEIGHT_KG = 500;
const MIN_HEIGHT_CM = 50;
const MAX_HEIGHT_CM = 300;

/**
 * @param {number} weightKg
 * @param {'metric' | 'imperial'} unit
 */
export function formatWeightDisplay(weightKg, unit) {
    if (!Number.isFinite(weightKg)) return '';
    if (unit === 'imperial') {
        return (weightKg / 0.45359237).toFixed(1);
    }
    return String(Math.round(weightKg * 10) / 10);
}

/**
 * @param {number} heightCm
 * @param {'metric' | 'imperial'} unit
 * @returns {{ feet: string, inches: string }}
 */
export function heightToImperial(heightCm) {
    if (!Number.isFinite(heightCm) || heightCm <= 0) {
        return { feet: '', inches: '' };
    }
    const totalInches = heightCm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches - feet * 12);
    return { feet: String(feet), inches: String(inches) };
}

/**
 * @param {number} feet
 * @param {number} inches
 */
export function imperialToHeightCm(feet, inches) {
    const totalInches = feet * 12 + inches;
    return totalInches * 2.54;
}

/**
 * @param {number} weight
 * @param {'metric' | 'imperial'} unit
 */
export function weightToKg(weight, unit) {
    if (!Number.isFinite(weight)) return NaN;
    return unit === 'imperial' ? weight * 0.45359237 : weight;
}

/**
 * @param {number} bmi
 * @returns {BmiCategory}
 */
export function getBmiCategory(bmi) {
    if (bmi < 18.5) return 'underweight';
    if (bmi < 25) return 'normal';
    if (bmi < 30) return 'overweight';
    return 'obese';
}

/**
 * @param {number} weightKg
 * @param {number} heightCm
 */
export function validateBmiInputs(weightKg, heightCm) {
    if (!Number.isFinite(weightKg) || weightKg < MIN_WEIGHT_KG || weightKg > MAX_WEIGHT_KG) {
        return { valid: false, error: /** @type {BmiErrorCode} */ ('invalidWeight') };
    }
    if (!Number.isFinite(heightCm) || heightCm < MIN_HEIGHT_CM || heightCm > MAX_HEIGHT_CM) {
        return { valid: false, error: /** @type {BmiErrorCode} */ ('invalidHeight') };
    }
    return { valid: true };
}

/**
 * @param {number} weightKg
 * @param {number} heightCm
 */
export function calculateBmi(weightKg, heightCm) {
    const validation = validateBmiInputs(weightKg, heightCm);
    if (!validation.valid) {
        return { ok: false, error: validation.error };
    }

    const heightM = heightCm / 100;
    const bmi = weightKg / (heightM * heightM);
    const category = getBmiCategory(bmi);
    const healthy = getHealthyWeightRangeKg(heightCm);

    return {
        ok: true,
        bmi,
        category,
        weightKg,
        heightCm,
        healthyMinKg: healthy.min,
        healthyMaxKg: healthy.max,
    };
}

/**
 * @param {number} heightCm
 */
export function getHealthyWeightRangeKg(heightCm) {
    const heightM = heightCm / 100;
    const min = 18.5 * heightM * heightM;
    const max = 24.9 * heightM * heightM;
    return { min, max };
}

export const BMI_CATEGORY_STYLES = {
    underweight: {
        bar: 'bg-sky-500',
        text: 'text-sky-700 dark:text-sky-300',
        bg: 'bg-sky-50 dark:bg-sky-950/30',
        border: 'border-sky-200 dark:border-sky-800',
    },
    normal: {
        bar: 'bg-emerald-500',
        text: 'text-emerald-700 dark:text-emerald-300',
        bg: 'bg-emerald-50 dark:bg-emerald-950/30',
        border: 'border-emerald-200 dark:border-emerald-800',
    },
    overweight: {
        bar: 'bg-amber-500',
        text: 'text-amber-700 dark:text-amber-300',
        bg: 'bg-amber-50 dark:bg-amber-950/30',
        border: 'border-amber-200 dark:border-amber-800',
    },
    obese: {
        bar: 'bg-red-500',
        text: 'text-red-700 dark:text-red-300',
        bg: 'bg-red-50 dark:bg-red-950/30',
        border: 'border-red-200 dark:border-red-800',
    },
};
